/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import { getApiUrl } from "@/utils/baseUrl";
import { withSessionToken } from "@/utils/sessionToken";

export interface RouterStatus {
  id: string;
  name: string;
  sshHost: string;
  sshPort: number;
  sshUser: string;
  profile: RouterProfile;
  online: boolean;
  youtubeOk: boolean;
  servicesOk: boolean;
  dnsAdblockOk: boolean;
  dnsInternetOk: boolean;
  hostname?: string;
  uptime?: string;
  overlay?: string;
  memory?: string;
  youtubeLine?: string;
  dnsLine?: string;
  model?: string;
  kernel?: string;
  openwrt?: string;
  load1?: string;
  load5?: string;
  load15?: string;
  uptimeSec?: number;
  memoryStats?: RouterMemoryStats;
  storageStats?: RouterStorageStats;
  interfaces?: RouterInterfaceStats[];
  wifiRadios?: RouterWifiRadio[];
  cpu?: RouterCPUStats;
  wifiClients?: RouterWifiClient[];
  /** what the router agent did on its own (restarts, DNS switches), newest last */
  agentEvents?: { at: string; message: string }[];
  conntrack?: RouterConntrackStats;
  dnsMode?: string;
  defaultRoute?: string;
  latency?: RouterLatencyCheck[];
  logHealth?: RouterLogHealth;
  watchdog?: RouterWatchdogStatus;
  error?: string;
  checkedAt: string;
  rawOutput?: string;
}

export interface RouterProfile {
  displayName?: string;
  location?: string;
  owner?: string;
  purpose?: string;
  notes?: string;
}

export interface RouterMemoryStats {
  totalKb: number;
  usedKb: number;
  freeKb: number;
  availableKb: number;
  cachedKb: number;
  usedPercent: number;
}

export interface RouterStorageStats {
  filesystem: string;
  size: string;
  used: string;
  available: string;
  usePercent: number;
  mountpoint: string;
}

export interface RouterInterfaceStats {
  name: string;
  rxBytes: number;
  rxPackets: number;
  rxErrors: number;
  rxDrops: number;
  txBytes: number;
  txPackets: number;
  txErrors: number;
  txDrops: number;
}

export interface RouterWifiRadio {
  phy?: string;
  interface?: string;
  ssid?: string;
  channel?: string;
  width?: string;
  frequency?: string;
  txPower?: string;
  type?: string;
}

export interface RouterCPUStats {
  userPercent: number;
  systemPercent: number;
  idlePercent: number;
  busyPercent: number;
}

export interface RouterWifiClient {
  interface: string;
  mac: string;
  signalDbm?: number;
  rxRate?: string;
  txRate?: string;
  expectedThroughput?: string;
  inactive?: string;
}

export interface RouterConntrackStats {
  count: number;
  max: number;
  usePercent: number;
}

export interface RouterLatencyCheck {
  name: string;
  target: string;
  ok: boolean;
  timeMs?: string;
  line?: string;
}

export interface RouterLogHealth {
  issueCount: number;
  recent?: string[];
}

export interface RouterWatchdogStatus {
  dnsInstalled: boolean;
  cronRunning: boolean;
  lastRun?: string;
}

export interface RouterActionResponse {
  id: string;
  name: string;
  success: boolean;
  output: string;
  checkedAt: string;
}

export interface RouterCheckEvent {
  group: "ssh" | "youtube" | "services" | "done";
  name: string;
  target?: string;
  status: "running" | "ok" | "error" | "done";
  ok: boolean;
  detail?: string;
  checkedAt: string;
}

async function parseErrorOr(response: Response, fallback: string): Promise<never> {
  const errorData = await response.json().catch(() => ({}));
  throw new Error(errorData.error || errorData.output || fallback);
}

export async function getRouters(): Promise<RouterStatus[]> {
  const response = await fetch(getApiUrl("/routers"));
  if (!response.ok) return parseErrorOr(response, "Failed to fetch routers");
  const data = await response.json();
  return Array.isArray(data) ? data : [];
}

export async function getRouter(id: string): Promise<RouterStatus> {
  const response = await fetch(getApiUrl(`/routers/${id}`));
  if (!response.ok) return parseErrorOr(response, "Failed to fetch router");
  return response.json();
}

export async function fixRouterYouTube(id: string): Promise<RouterActionResponse> {
  const response = await fetch(getApiUrl(`/routers/${id}/fix-youtube`), {
    method: "POST",
  });
  if (!response.ok) return parseErrorOr(response, "Failed to fix YouTube");
  return response.json();
}

export async function backupRouter(id: string): Promise<RouterActionResponse> {
  const response = await fetch(getApiUrl(`/routers/${id}/backup`), {
    method: "POST",
  });
  if (!response.ok) return parseErrorOr(response, "Failed to backup router");
  return response.json();
}

export async function checkRouterDNS(id: string): Promise<RouterActionResponse> {
  const response = await fetch(getApiUrl(`/routers/${id}/dns`));
  if (!response.ok) return parseErrorOr(response, "Failed to check router DNS");
  return response.json();
}

export async function installRouterDNSWatchdog(id: string): Promise<RouterActionResponse> {
  const response = await fetch(getApiUrl(`/routers/${id}/dns-watchdog`), {
    method: "POST",
  });
  if (!response.ok) return parseErrorOr(response, "Failed to install DNS watchdog");
  return response.json();
}

export async function restartRouterService(id: string, service: "zapret" | "dns" | "ssh-tunnel"): Promise<RouterActionResponse> {
  const response = await fetch(getApiUrl(`/routers/${id}/restart/${service}`), {
    method: "POST",
  });
  if (!response.ok) return parseErrorOr(response, "Failed to restart router service");
  return response.json();
}

export async function updateRouterProfile(id: string, profile: RouterProfile): Promise<RouterStatus> {
  const response = await fetch(getApiUrl(`/routers/${id}/profile`), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(profile),
  });
  if (!response.ok) return parseErrorOr(response, "Failed to update router profile");
  return response.json();
}

export function getRouterCheckStreamUrl(id: string): string {
  return withSessionToken(getApiUrl(`/routers/${id}/check-stream`));
}

/** Live state pushed by a router's nyrvex-router-agent (GET /routers/agent). */
export interface RouterAgentState {
  id: string;
  connected: boolean;
  lastSeen: string;
  board?: { hostname?: string; kernel?: string; model?: string; release?: string; agent?: string };
  telemetry?: {
    uptime?: number;
    load1?: number;
    load5?: number;
    load15?: number;
    memTotalKb?: number;
    memAvailableKb?: number;
    tempC?: number | null;
    cpuUser?: number;
    cpuSystem?: number;
    cpuIdle?: number;
    conntrack?: number;
    conntrackMax?: number;
    fs?: { name: string; totalKb: number; usedKb: number }[];
    interfaces?: { name: string; rxBytes: number; txBytes: number; rxPackets: number; txPackets: number; rxErrors: number; txErrors: number; rxDropped: number; txDropped: number }[];
  };
  clients?: {
    radios?: { iface: string; ssid?: string; channel?: number; freq?: number; clients: number; enabled?: boolean }[];
    wifi?: { mac: string; iface: string; signalDbm?: number | null; ip?: string; host?: string; rxKbps?: number; txKbps?: number; standard?: string }[];
  };
  services?: { procd?: { name: string; running: boolean; program: string }[]; procs?: string[] };
  health?: { pings?: { name: string; target: string; ok: boolean; ms: number | null }[]; dnsAdblockOk?: boolean; dnsInternetOk?: boolean };
  youtube?: { ok: number; total: number; healthy: boolean; hosts?: { name: string; ok: boolean; code: string }[] };
  events?: { at: string; message: string }[];
}

export async function getRouterAgents(): Promise<RouterAgentState[]> {
  const response = await fetch(getApiUrl("/routers/agent"));
  if (!response.ok) return [];
  const data = await response.json();
  return Array.isArray(data) ? data : [];
}

const fmtKb = (kb: number) => (kb >= 1024 ? `${(kb / 1024).toFixed(1)}M` : `${kb}K`);

/**
 * Lays an agent's live numbers over a router status. The agent is the better
 * source for everything it reports (fresh every 5 s, no SSH round trip); the
 * SSH-only checks (YouTube, DNS, services) are left as they were.
 */
export function withAgent(router: RouterStatus, agent: RouterAgentState | undefined): RouterStatus {
  if (!agent || !agent.connected || !agent.telemetry) return router;
  const t = agent.telemetry;
  const out: RouterStatus = { ...router, online: true };
  if (agent.board?.hostname) out.hostname = agent.board.hostname;
  if (agent.board?.model) out.model = agent.board.model;
  if (agent.board?.kernel) out.kernel = agent.board.kernel;
  if (agent.board?.release) out.openwrt = agent.board.release;
  if (t.uptime) out.uptimeSec = t.uptime;
  if (t.load1 !== undefined) {
    out.load1 = t.load1.toFixed(2);
    out.load5 = (t.load5 ?? 0).toFixed(2);
    out.load15 = (t.load15 ?? 0).toFixed(2);
  }
  if (t.memTotalKb && t.memAvailableKb !== undefined) {
    const used = t.memTotalKb - t.memAvailableKb;
    out.memoryStats = {
      totalKb: t.memTotalKb,
      usedKb: used,
      freeKb: t.memAvailableKb,
      availableKb: t.memAvailableKb,
      cachedKb: 0,
      usedPercent: Math.round((used / t.memTotalKb) * 100),
    };
  }
  const flash = t.fs?.find((f) => f.name === "flash");
  if (flash && flash.totalKb > 0) {
    out.storageStats = {
      filesystem: "overlay",
      size: fmtKb(flash.totalKb),
      used: fmtKb(flash.usedKb),
      available: fmtKb(flash.totalKb - flash.usedKb),
      usePercent: Math.round((flash.usedKb / flash.totalKb) * 100),
      mountpoint: "/",
    };
  }
  if (t.interfaces?.length) {
    out.interfaces = t.interfaces.map((i) => ({
      name: i.name,
      rxBytes: i.rxBytes,
      rxPackets: i.rxPackets,
      rxErrors: i.rxErrors,
      rxDrops: i.rxDropped,
      txBytes: i.txBytes,
      txPackets: i.txPackets,
      txErrors: i.txErrors,
      txDrops: i.txDropped,
    }));
  }
  if (t.cpuIdle !== undefined) {
    out.cpu = {
      userPercent: Math.round(t.cpuUser ?? 0),
      systemPercent: Math.round(t.cpuSystem ?? 0),
      idlePercent: Math.round(t.cpuIdle),
      busyPercent: Math.round(100 - t.cpuIdle),
    };
  }
  if (t.conntrack !== undefined && t.conntrackMax) {
    out.conntrack = { count: t.conntrack, max: t.conntrackMax, usePercent: Math.round((t.conntrack / t.conntrackMax) * 100) };
  }
  if (agent.events?.length) out.agentEvents = agent.events;
  if (agent.clients?.radios) {
    out.wifiRadios = agent.clients.radios.map((r) => ({
      interface: r.iface,
      ssid: r.ssid,
      channel: r.channel ? String(r.channel) : undefined,
      frequency: r.freq ? `${(r.freq / 1000).toFixed(3)} GHz` : undefined,
      type: r.freq && r.freq > 4000 ? "5 GHz" : "2.4 GHz",
    }));
  }
  if (agent.clients?.wifi) {
    out.wifiClients = agent.clients.wifi.map((c) => ({
      interface: c.iface,
      mac: c.mac,
      signalDbm: c.signalDbm ?? undefined,
      rxRate: c.rxKbps ? `${(c.rxKbps / 1000).toFixed(1)} Mbit/s` : undefined,
      txRate: c.txKbps ? `${(c.txKbps / 1000).toFixed(1)} Mbit/s` : undefined,
    }));
  }
  const h = agent.health;
  if (h) {
    if (h.dnsAdblockOk !== undefined) out.dnsAdblockOk = h.dnsAdblockOk;
    if (h.dnsInternetOk !== undefined) out.dnsInternetOk = h.dnsInternetOk;
    if (h.pings?.length) {
      out.latency = h.pings.map((p) => ({ name: p.name, target: p.target, ok: p.ok, timeMs: p.ms === null ? undefined : p.ms.toFixed(1) }));
    }
  }
  if (agent.youtube) {
    out.youtubeOk = agent.youtube.healthy;
    out.youtubeLine = (agent.youtube.hosts ?? []).map((x) => `${x.name} ${x.ok ? "OK" : "FAIL"} HTTP/${x.code}`).join(" / ") + ` / YOUTUBE_OK=${agent.youtube.ok}/${agent.youtube.total}`;
  }
  // every procd service that exists must be running; zapret's nfqws counts as a process
  const procd = agent.services?.procd;
  if (procd) {
    // a service with no program is a one-shot config hook (firewall, system, ...); a few named ones run once at boot
    const oneShots = ["urandom_seed", "wake-pve"];
    const wanted = procd.filter((s) => s.program && !oneShots.includes(s.name));
    out.servicesOk = wanted.length > 0 && wanted.every((s) => s.running);
  }
  return out;
}
